import MsDyn365, {
    IActionContext,
    ITelemetry,
    ICreateActionContext,
    IGeneric,
    IAny,
    IRequestContext,
    IImageSettings
} from '@msdyn365-commerce/core';
import { INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import {
    CommerceProperty,
    CommercePropertyValue,
    FeatureState,
    OrgUnitLocation,
    ProductSearchResult,
    SimpleProduct,
    SortColumn
} from '@msdyn365-commerce/retail-proxy';
import { ICommerceApiSettings, IComponentProps, ICoreContext } from '@msdyn365-commerce/core-internal';
import storeSelectorStateDataAction from '../modules/mmx-store-selector/state-manager/store-selector-state-manager-data-action';
import { IStoreSelectorStateManager, MapStoreSelectorStateManager } from '../modules/mmx-store-selector/state-manager';
import {
    GenericInput,
    getDeliveryOptionsForSelectedVariant,
    GetDeliveryOptionsForSelectedVariantInput,
    getOrgUnitLocationsByArea,
    GetOrgUnitLocationsByAreaInput,
    generateProductImageUrl as defaultGenerateProductImageUrl,
    ArrayExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import React from 'react';
import { getByIdAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

export const MAX_NUM_GRID_DISPLAY_IMAGES = 4;

export const CURRENCY_DECIMAL_PLACES: number = 2;

export const PRICE_FREE: number = 0;

export const ISO_FORMATTED_DATE__TIME_DELINEATOR: string = 'T';

export interface ISortByViewProps {
    SortingContainer: INodeProps;
    sortByDropDown?: React.ReactNode;
}

export enum CartLineExtensionProperties {
    PriceBeatCompetitorPrice = 'Memx_PriceBeatCompetitorPrice'
}

export const sortOptions = {
    sortByOptionRelevanceDesc: 'Ranking-Desc',
    sortByOptionRatingAsc: 'AverageRating-Asc',
    sortByOptionRatingDesc: 'AverageRating-Desc',
    sortByOptionNameAsc: 'Name-Asc',
    sortByOptionNameDesc: 'Name-Desc',
    sortByOptionPriceAsc: 'Price-Asc',
    sortByOptionPriceDesc: 'Price-Desc',
    sortByOptionBestSelling: 'BestSelling',
    sortByOptionNewScore: 'NewScore',
    sortByOptionTrendingScore: 'TrendingScore'
};
export interface IItemSearchResultsWithCount<T> {
    products: T[];
    count: number;
    channelInventoryConfigurationId?: number;
    inventoryAwareSortableAttributeId?: number;
}
export enum ProductListInventoryFilteringOptions {
    HideOOS = 'hideOOS',
    SortOOS = 'sortOOS',
    Default = 'default'
}

export interface ILineItem {
    data?: any;
}

export interface IGroupedItemLine<T> {
    item: T;
    buildItems?: T[];
    warranty?: T;
}

export enum ProductLineDataType {
    ICartlinesViewProps = 'cartline',
    IFlyoutCartLineItemViewProps = 'cartline',
    ISalesLine = 'salesLine'
}

export enum ProductLineCommerceProperties {
    isWarrantyLine = 'Memx_IsWarrantyLine',
    warrantableLineNumber = 'Memx_WarrantableLineNumber',
    warrantyPeriod = 'Memx_WarrantyPeriod',
    isBuildLine = 'Memx_CustomBuilds_IsCustomBuildChild',
    isBuildParent = 'Memx_CustomBuilds_IsCustomBuild',
    buildParentLineNumber = 'Memx_CustomBuilds_ParentLineNum',
    buildParentName = 'Memx_CustomBuilds_Name',
    buildSlotId = 'Memx_ConfigurationSlotId',
    productFullName = 'Memx_ProductFullName',
    productStockStatus = 'Memx_ProductStockStatus'
}

export interface IMmxComponentProps<T> extends IComponentProps<T> {
    telemetryContent?: ITelemetryContent;
}

export interface MappedSearchConfiguration {
    key: string;
    sortColumn: SortColumn;
}

export interface ITitleViewProps {
    TitleContainer: INodeProps;
    title: ISearchResultTitle;
}

/**
 * Title component for search result container.
 */
export interface ISearchResultTitle {
    titlePrefix?: React.ReactNode;
    titleText?: React.ReactNode;
    titleCount?: React.ReactNode;
}

export function getValueFromExtensionProperties<T>(
    item: { ExtensionProperties?: CommerceProperty[] },
    propertyName: string
): CommercePropertyValue | undefined {
    return item.ExtensionProperties?.find((commerceProp: CommerceProperty) => commerceProp.Key === propertyName)?.Value;
}

/**
 * Assigns given pickup location to given product.
 */
export const setPickupLocationForProduct = async (
    product: SimpleProduct,
    quantity: number,
    onLocationSelected: (orgUnitLocation: OrgUnitLocation, deliveryMode: string) => Promise<void>,
    storeSelectorId: string,
    modalCloseButtonReference: React.RefObject<HTMLButtonElement>,
    channelId: number,
    actionContext: IActionContext,
    telemetry?: ITelemetry
): Promise<undefined> => {
    const storeSelectorStateManager = await storeSelectorStateDataAction(
        new GenericInput<IStoreSelectorStateManager>('mmx_mapSSSM', new MapStoreSelectorStateManager(), 'IStoreSelectorStateManager'),
        actionContext
    ).catch((error: Error) => {
        if (telemetry) {
            telemetry.error(error.message);
            telemetry.debug('Error finding store selector state manager');
        }
    });

    if (storeSelectorStateManager) {
        const deliveryOptions = await getDeliveryOptionsForSelectedVariant(
            new GetDeliveryOptionsForSelectedVariantInput(product.RecordId, channelId, undefined, undefined, true),
            actionContext
        );

        const locations = await getOrgUnitLocationsByArea(
            new GetOrgUnitLocationsByAreaInput(undefined, undefined, 0, 0, true),
            actionContext
        );

        if (locations && locations.length) {
            if (locations.length > 1) {
                await storeSelectorStateManager
                    .openDialog({
                        product: product,
                        id: storeSelectorId ?? 'mmx-store-selector',
                        parentElementRef: modalCloseButtonReference,
                        deliveryOptions: deliveryOptions,
                        additionalProperties: {
                            amountSelected: quantity,
                            isStockedProuct: product.Behavior?.IsStockedProduct
                        },
                        onLocationSelected: onLocationSelected
                    })
                    .catch((error: any) => {
                        if (telemetry) {
                            telemetry.warning(error);
                            telemetry.debug('[order-template-lines] Error trying to Update Cart Line from the order template lines');
                        }
                    });
                return Promise.resolve(undefined);
            } else {
                if (locations[0].OrgUnitAvailability && locations[0].OrgUnitAvailability?.OrgUnitLocation) {
                    const defaultDeliveryOption = deliveryOptions?.DeliveryOptions ? deliveryOptions.DeliveryOptions[0].Code : undefined;
                    if (defaultDeliveryOption) {
                        onLocationSelected(locations[0].OrgUnitAvailability.OrgUnitLocation, defaultDeliveryOption).catch(
                            (error: Error) => {
                                if (telemetry) {
                                    telemetry.warning(error.message);
                                    telemetry.debug('[utils] onLocationSelected callback ran into an error');
                                }
                            }
                        );
                        return Promise.resolve(undefined);
                    } else {
                        return Promise.reject('Could not find value delivery options');
                    }
                } else {
                    return Promise.reject('No valid org unit availability object');
                }
            }
        } else {
            return Promise.reject('No location found');
        }
    }
    return Promise.reject('Could not set pickup location');
};
/**
 * Utility function to extract the active productId in the following priority:
 * First query param (productId), then UrlToken (itemId), then module config.
 * Shamelessly stolen from msdyn commerce modules.
 * @param inputData The Action Input data.
 */
export const getSelectedProductIdFromActionInput = (inputData: ICreateActionContext<IGeneric<IAny>>): string | undefined => {
    if (inputData && inputData.requestContext && inputData.requestContext.query && inputData.requestContext.query['product-id']) {
        return inputData.requestContext.query['product-id'];
    } else if (inputData && inputData.requestContext && inputData.requestContext.query && inputData.requestContext.query.productId) {
        return inputData.requestContext.query.productId;
    } else if (
        inputData &&
        inputData.requestContext &&
        inputData.requestContext.urlTokens &&
        inputData.requestContext.urlTokens.pageType &&
        inputData.requestContext.urlTokens.pageType !== 'Category' &&
        inputData.requestContext.urlTokens.itemId
    ) {
        return inputData.requestContext.urlTokens.itemId;
    } else if (inputData && inputData.config && inputData.config.productId) {
        return inputData.config.productId;
    }
    //mmx-feature --start
    else if (_doesUrlContainCustomProductIdToken(inputData)) {
        return getValueFromUrl(PRODUCT_ID_URL_TOKEN_PAGES[0], inputData);
    }
    //mmx-feature --end
    return undefined;
};

const PRODUCT_ID_URL_TOKEN_PAGES = ['clearance-item'];

function _doesUrlContainCustomProductIdToken(inputData: ICreateActionContext<IGeneric<IAny>>): boolean {
    const urlSegments = inputData.requestContext.url.requestUrl.pathname.split('/');
    if (urlSegments.length <= 1) {
        return false;
    }
    const product = urlSegments[urlSegments.findIndex(urlSeg => PRODUCT_ID_URL_TOKEN_PAGES.some(token => token === urlSeg)) + 1];
    return !!product;
}

export function getValueFromUrl(precedingSegmentString: string, inputData: ICreateActionContext<IGeneric<IAny>>): string | undefined {
    const pathSegments = inputData.requestContext.url.requestUrl.pathname.split('/');
    if (pathSegments.length <= 1) {
        return;
    }
    return pathSegments[pathSegments.findIndex(pathSeg => pathSeg === precedingSegmentString) + 1];
}

/**
 * Generates a Image URL for a product based on data return from retail server.
 * @param product The Product returned by Retail Server.
 * @param apiSettings
 * @param trimDimensionsFromProductImageUrl optional parameter from config. If not set to true, use default url functionality.
 */
export const generateProductImageUrl = (
    product: SimpleProduct | ProductSearchResult,
    apiSettings: ICommerceApiSettings,
    trimDimensionsFromProductImageUrl?: boolean
): string | undefined => {
    if (!trimDimensionsFromProductImageUrl) {
        return defaultGenerateProductImageUrl(product, apiSettings);
    }
    const fullImagePath = /^(\/\/|^)http/;
    const PRODUCT_DELINEATOR_INDEX = 0;
    const PRODUCT_SEQ_INDEX = 4;

    const imageUrl = product.PrimaryImageUrl;
    if (imageUrl) {
        if (fullImagePath.test(imageUrl)) {
            return imageUrl;
        }
        const imageUrlDecoded = decodeURIComponent(imageUrl);
        const imageComponents = imageUrlDecoded.split(' ^');
        let imageUrlRebuilt = imageComponents[+PRODUCT_DELINEATOR_INDEX];
        if (imageComponents.length > 1) {
            imageUrlRebuilt += imageComponents[+PRODUCT_SEQ_INDEX];
        }
        // Images hosted in Retail Server must be encoded and joined with the base image url
        return apiSettings.baseImageUrl + encodeURIComponent(imageUrlRebuilt);
    }
    return undefined;
};

/**
 * Gets item at provided cache key from the cache of a state data action. If multiple results are found, return only the first one.
 * @param context The request context
 * @param cacheObjectType The object type returned by the state data action
 * @param cacheKey The name of the state data action
 */
export function getSingleItemFromStateAction<T>(context: IActionContext, cacheObjectType: string, cacheKey: string): T | undefined {
    const result = context.get<T>(cacheObjectType, cacheKey);
    if (!result) {
        return undefined;
    } else if (Object.prototype.toString.call(result) !== '[object Array]') {
        //@ts-ignore
        return result;
    }
    //@ts-ignore
    return result[0];
}

/**
 * Gets the current URL of the page from the request context.
 * @param requestContext The request context.
 */
export const getCurrentUrl = (requestContext: IRequestContext): URL => {
    if (MsDyn365.isBrowser) {
        return new URL(window.location.href);
    }

    // NOTE: Typing on requestURL is incorrect
    if (requestContext.url.requestUrl.href) {
        return new URL(requestContext.url.requestUrl.href);
    }
    return new URL(<string>(<unknown>requestContext.url.requestUrl));
};

export const getImageUrlFromMaster = async (context: IActionContext, product: SimpleProduct): Promise<string> => {
    let imageUrl: string = product.PrimaryImageUrl ?? '';
    if (!imageUrl && product.MasterProductId) {
        const productMaster = await getByIdAsync(
            { callerContext: context, queryResultSettings: { Paging: { Top: 1, Skip: 0 } } },
            product.MasterProductId,
            context.requestContext.apiSettings.channelId
        );

        if (productMaster.PrimaryImageUrl && context.requestContext.apiSettings.imageResizerEndPoint) {
            imageUrl = productMaster.PrimaryImageUrl.includes(context.requestContext.apiSettings.imageResizerEndPoint)
                ? productMaster.PrimaryImageUrl
                : `${context.requestContext.apiSettings.imageResizerEndPoint}/${productMaster.PrimaryImageUrl}`;
        } else {
            imageUrl = product.PrimaryImageUrl ?? '';
        }
    }

    return imageUrl;
};

/**
 * Checks if the ChannelMultipleCatalogsFeature is enabled in HQ.
 * @param featureState a list of feature states. This list must be pulled from HQ, as the Commerce Runtime does not have this information.
 * @returns boolean indicating if the feature is enabled or not.
 */
export const _isChannelMultipleCatalogsFeatureEnabled = (featureState?: FeatureState[]): boolean => {
    let isMultipleCatalogFeatureEnabledInHq = false;
    if (featureState) {
        isMultipleCatalogFeatureEnabledInHq =
            featureState.find(feature => feature.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature')?.IsEnabled || false;
    }
    return isMultipleCatalogFeatureEnabledInHq;
};

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
export const getInventoryLabel = (
    channelInventoryConfigurationId: number | undefined,
    product: ProductSearchResult
): string | undefined => {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
};

/**
 * Gets the value of a URL parameter by its name.
 * @param args The arguments containing the request context.
 * @param pathSegmentName The name of the path segment to retrieve.
 * @returns The value of the URL parameter.
 */
export const getUrlParameters = (args: { requestContext: IRequestContext }, pathSegmentName: string): string => {
    const pathSegments = args.requestContext.url.requestUrl.pathname.split('/');
    return pathSegments[pathSegments.findIndex(pathSeg => pathSeg === pathSegmentName) + 1];
};

/**
 *  Removes the format value from each of an image settings viewports
 */
export const removeViewportFormat = (imageSettings: IImageSettings): IImageSettings => {
    const viewportFormatRegex = /&?f=[A-Za-z]+/;
    if (imageSettings) {
        if (imageSettings.viewports?.lg?.q) {
            imageSettings.viewports.lg.q = imageSettings.viewports.lg.q.replace(viewportFormatRegex, '');
        }

        if (imageSettings.viewports?.md?.q) {
            imageSettings.viewports.md.q = imageSettings.viewports.md.q.replace(viewportFormatRegex, '');
        }

        if (imageSettings.viewports?.sm?.q) {
            imageSettings.viewports.sm.q = imageSettings.viewports.sm.q.replace(viewportFormatRegex, '');
        }

        if (imageSettings.viewports?.xl?.q) {
            imageSettings.viewports.xl.q = imageSettings.viewports.xl.q.replace(viewportFormatRegex, '');
        }

        if (imageSettings.viewports?.xs?.q) {
            imageSettings.viewports.xs.q = imageSettings.viewports.xs.q.replace(viewportFormatRegex, '');
        }
    }

    return imageSettings;
};

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export function _getViewport(context: ICoreContext): GridSize {
    // Always render in mobile viewport on a mobile device
    if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
        return 'xs';
    }

    if (MsDyn365.isBrowser && window.innerWidth) {
        const gridSettings = context.request.gridSettings;
        if (gridSettings) {
            if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                return 'xs';
            } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                return 'sm';
            } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                return 'md';
            } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                return 'lg';
            }
            return 'xl';
        }
    }

    return 'xs';
}
